import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography as MuiTypography, useTheme } from '@mui/material';
import { IconAlertTriangle, IconChevronRight } from '@tabler/icons-react';

import { Typography } from '@zendesk/forethought-elements';
import { auditErrorEvents, AuditEvent, ConversationAudit } from './auditData';
import { CopyableId } from './CopyableId';

// One row per error: what the error service called it, its id, its severity, and
// a jump to the turn it failed in. A single "go to it" would only ever reach the
// first error and strand the rest.
const ErrorRow = ({
  errorOrdinal,
  event,
  exchangeIndex,
  onJump,
}: {
  errorOrdinal: number;
  event: AuditEvent;
  exchangeIndex: number;
  onJump: (exchangeIndex: number, errorOrdinal: number) => void;
}) => {
  const { t } = useTranslation('intent-conversation-analytics');
  const { palette } = useTheme();
  const error = event.error;

  return (
    <Box
      sx={{
        alignItems: 'center',
        bgcolor: palette.colors.white,
        borderRadius: '8px',
        display: 'flex',
        gap: '8px',
        px: '12px',
        py: '8px',
      }}
    >
      <IconAlertTriangle color={palette.colors.red[500]} size={14} />
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          flexDirection: 'column',
          minWidth: 0,
        }}
      >
        <Typography noWrap variant='font12Medium'>
          {event.category ?? event.detail}
        </Typography>
        <Box sx={{ alignItems: 'center', display: 'flex', gap: '4px' }}>
          {error && (
            <CopyableId
              ariaLabel={t('conversationAudit.copyErrorId', {
                errorId: error.error_id,
              })}
              color={palette.colors.grey[600]}
              value={error.error_id}
            />
          )}
          <Typography color={palette.colors.grey[600]} variant='font11'>
            {event.severity
              ? t('conversationAudit.severityAndTurn', {
                  severity: event.severity,
                  turn: exchangeIndex + 1,
                })
              : t('conversationAudit.turnLabel', { turn: exchangeIndex + 1 })}
          </Typography>
        </Box>
      </Box>
      <Box
        component='button'
        onClick={() => onJump(exchangeIndex, errorOrdinal)}
        sx={{
          alignItems: 'center',
          background: palette.colors.white,
          border: `1px solid ${palette.colors.slate[300]}`,
          borderRadius: '16px',
          cursor: 'pointer',
          display: 'flex',
          flexShrink: 0,
          font: 'inherit',
          gap: '2px',
          px: '10px',
          py: '4px',
        }}
      >
        <Typography variant='font11'>
          {t('conversationAudit.turnLabel', { turn: exchangeIndex + 1 })}
        </Typography>
        <IconChevronRight color={palette.colors.grey[700]} size={12} />
      </Box>
    </Box>
  );
};

// The error service's own wording is the only wording in the error path.
export const ErrorRollupCard = ({
  audit,
  onJump,
}: {
  audit: ConversationAudit;
  onJump: (exchangeIndex: number, errorOrdinal: number) => void;
}) => {
  const { t } = useTranslation('intent-conversation-analytics');
  const { palette } = useTheme();
  const [isExpanded, setIsExpanded] = useState(false);
  const errors = auditErrorEvents(audit);

  if (audit.state === 'healthy' || errors.length === 0) {
    return null;
  }

  const shownErrors = isExpanded ? errors : errors.slice(0, 1);
  const hiddenCount = errors.length - 1;

  return (
    <Box
      sx={{
        bgcolor: palette.colors.red[100],
        border: `1px solid ${palette.colors.red[300]}`,
        borderRadius: '8px',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        p: '16px',
      }}
    >
      <Box sx={{ alignItems: 'center', display: 'flex', gap: '8px' }}>
        <Box
          sx={{
            alignItems: 'center',
            bgcolor: palette.colors.red[200],
            borderRadius: '16px',
            display: 'flex',
            gap: '4px',
            px: '8px',
            py: '2px',
          }}
        >
          <IconAlertTriangle color={palette.colors.red[500]} size={13} />
          <MuiTypography
            color={palette.colors.red[700]}
            sx={{ fontWeight: 600 }}
            variant='font12Medium'
          >
            {t('conversationAudit.errorCount', { count: errors.length })}
          </MuiTypography>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {shownErrors.map(({ event, exchangeIndex }, errorOrdinal) => (
          <ErrorRow
            errorOrdinal={errorOrdinal}
            event={event}
            exchangeIndex={exchangeIndex}
            key={event.error?.error_id ?? `${event.timestamp}-${exchangeIndex}`}
            onJump={onJump}
          />
        ))}
        {hiddenCount > 0 && (
          <Box
            aria-expanded={isExpanded}
            component='button'
            onClick={() => setIsExpanded(expanded => !expanded)}
            sx={{
              alignItems: 'center',
              bgcolor: palette.colors.white,
              border: 0,
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              font: 'inherit',
              justifyContent: 'center',
              px: '12px',
              py: '6px',
            }}
          >
            <Typography color={palette.colors.red[700]} variant='font12Medium'>
              {isExpanded
                ? t('conversationAudit.hideErrors')
                : t('conversationAudit.moreErrors', { count: hiddenCount })}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};
